/**
 * IMBOX shoe protection upsell in cart.
 * Listens for toggle changes via delegation, adds/removes protection product
 * and refreshes cart section.
 */
import { fetchConfig } from '../../scripts/utils';
import { CartUpdateEvent } from './events';
import { morphSection } from '../../scripts/section-renderer';
import { reconcileCartDrawerPanels } from '../../scripts/reconcile-cart-drawer';

const PROP_KEY = '_imbox_for_key';
const PROP_FOR_VARIANT_ID = '_imbox_for_variant_id';
const PROP_FOR_LINE_INDEX = '_imbox_for_line_index';
const PROP_FOR_TITLE = 'Protected product';

/**
 * Collect section IDs from all cart-items-component instances (for section re-render).
 * @returns {string[]}
 */
function getCartSectionIds() {
  const components = document.querySelectorAll('cart-items-component');
  const ids = new Set();
  components.forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * Add protection product to cart and refresh sections.
 * @param {string} variantId - Protection variant GID or numeric ID.
 * @param {string} lineKey - Parent line item key.
 * @param {string[]} sectionIds - Section IDs to request in response.
 * @returns {Promise<{ sections?: Record<string, string> }>}
 */
/**
 * @param {string} variantId - Protection variant ID
 * @param {string} lineKey - Parent shoe cart line key (for cart logic)
 * @param {string[]} sectionIds - Section IDs to request
 * @param {{ variantId?: string, lineIndex?: string, title?: string }} [forOrder] - Data for order/fulfillment (persists on order)
 */
async function addImboxToCart(variantId, lineKey, sectionIds, forOrder = {}) {
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  formData.append('parent_line_key', lineKey);
  formData.append(`properties[${PROP_KEY}]`, lineKey);
  if (forOrder.variantId) formData.append(`properties[${PROP_FOR_VARIANT_ID}]`, String(forOrder.variantId));
  if (forOrder.lineIndex) formData.append(`properties[${PROP_FOR_LINE_INDEX}]`, String(forOrder.lineIndex));
  if (forOrder.title) formData.append(`properties[${PROP_FOR_TITLE}]`, forOrder.title);
  formData.append('sections', sectionIds.join(','));
  formData.append('sections_url', window.location.pathname);

  const res = await fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('javascript', { body: formData }),
    headers: {
      ...fetchConfig('javascript', { body: formData }).headers,
      Accept: 'application/json',
    },
  });
  const data = await res.json();
  if (data.status && data.status !== 200) {
    throw new Error(data.description || data.message || 'Failed to add protection');
  }
  return data;
}

/**
 * Remove the protection line from cart by its line item key (not index) so we
 * never remove the parent product. Sets quantity to 0 and refresh sections.
 * @param {string} lineItemKey - The protection line item's key (unique per line).
 * @param {string[]} sectionIds - Section IDs to request in response.
 * @returns {Promise<{ sections?: Record<string, string> }>}
 */
async function removeImboxFromCart(lineItemKey, sectionIds) {
  const body = JSON.stringify({
    id: lineItemKey,
    quantity: 0,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const res = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const text = await res.text();
  const data = JSON.parse(text);
  if (data.errors) {
    throw new Error(data.errors);
  }
  return data;
}

function initCartImbox() {
  document.addEventListener('change', (e) => {
    const toggle = e.target?.closest?.('[data-imbox-toggle]');
    if (!toggle || !toggle.closest('[data-imbox]')) return;

    const container = toggle.closest('[data-imbox]');
    const sectionId = container.dataset.sectionId;
    const variantId = container.dataset.variantId?.trim();
    const lineKey = container.dataset.lineKey;
    const imboxLineIndex = container.dataset.imboxLineIndex?.trim();
    const imboxLineKey = container.dataset.imboxLineKey?.trim();
    const forVariantId = container.dataset.imboxForVariantId?.trim();
    const forLineIndex = container.dataset.imboxForLineIndex?.trim();
    const forTitle = container.dataset.imboxForTitle?.trim();

    if (!sectionId || !lineKey) return;
    if (!variantId) return; /* no protection product configured – toggle should be disabled */

    const sectionIds = getCartSectionIds();
    if (sectionIds.length === 0) return;

    const checked = toggle.checked;
    const forOrder = { variantId: forVariantId, lineIndex: forLineIndex, title: forTitle };

    if (checked) {
      addImboxToCart(variantId, lineKey, sectionIds, forOrder)
        .then((data) => {
          if (data.sections) {
            document.dispatchEvent(new CartUpdateEvent({}, sectionId, { source: 'cart-imbox', sections: data.sections }));
            Promise.all(Object.keys(data.sections).map((id) => morphSection(id, data.sections[id]))).then(() => {
              reconcileCartDrawerPanels();
            });
          }
        })
        .catch((err) => {
          console.error('IMBOX add failed:', err);
          toggle.checked = false;
        });
    } else {
      if (!imboxLineKey) {
        console.warn('IMBOX: no line key to remove');
        return;
      }
      removeImboxFromCart(imboxLineKey, sectionIds)
        .then((data) => {
          if (data.sections) {
            document.dispatchEvent(new CartUpdateEvent({}, sectionId, { source: 'cart-imbox', sections: data.sections }));
            Promise.all(Object.keys(data.sections).map((id) => morphSection(id, data.sections[id]))).then(() => {
              reconcileCartDrawerPanels();
            });
          }
        })
        .catch((err) => {
          console.error('IMBOX remove failed:', err);
          toggle.checked = true;
        });
    }
  });
}

initCartImbox();
